import { useLanguage } from "@/lib/LanguageContext";
import { useReveal } from "@/lib/useReveal";
import { Compass, Zap, Droplets, Flame, Package, Truck, ArrowUpRight } from "lucide-react";

const serviceMeta = [
  {
    key: "consultation",
    Icon: Compass,
    span: "md:col-span-12 lg:col-span-8",
    big: true,
    image:
      "/img/service-consultation.jpg",
  },
  {
    key: "electrical",
    Icon: Zap,
    span: "md:col-span-6 lg:col-span-4",
  },
  {
    key: "furniture",
    Icon: Package,
    span: "md:col-span-6 lg:col-span-4",
  },
  {
    key: "plumbing",
    Icon: Droplets,
    span: "md:col-span-6 lg:col-span-4",
  },
  {
    key: "heating",
    Icon: Flame,
    span: "md:col-span-6 lg:col-span-4",
  },
  {
    key: "service",
    Icon: Truck,
    span: "md:col-span-12 lg:col-span-12",
    wide: true,
    image:
      "/img/service-mobile.jpg",
  },
];

export default function Services() {
  const { t } = useLanguage();
  const ref = useReveal();

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative px-6 sm:px-12 lg:px-24 py-24 sm:py-32"
    >
      <div ref={ref} className="reveal max-w-3xl mb-16">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D97736] mb-4">
          {t.services.overline}
        </p>
        <h2 className="font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-6">
          {t.services.title}
        </h2>
        <p className="text-[#A0AAB2] text-lg leading-relaxed">
          {t.services.subtitle_pre}
          <em className="italic text-white">{t.services.subtitle_em}</em>
          {t.services.subtitle_post}{" "}
          {t.services.subtitle_line2}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 auto-rows-[minmax(260px,auto)]">
        {serviceMeta.map(({ key, Icon, span, big, tall, wide, image }) => {
          const data = t.services.items[key];
          const isFeature = big || wide;
          return (
            <a
              key={key}
              href="#contact"
              data-testid={`service-${key}`}
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById("contact");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className={`group relative overflow-hidden col-span-1 ${span} bg-[#14161A] border border-white/5 hover:border-white/20 transition-all duration-500 hover:-translate-y-1 cursor-pointer`}
            >
              {image && (
                <div className="absolute inset-0">
                  <img
                    src={image}
                    alt=""
                    className="w-full h-full object-cover opacity-30 group-hover:opacity-40 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#0B0C10] via-[#0B0C10]/80 to-transparent" />
                </div>
              )}

              <div
                className={`relative h-full flex flex-col justify-between ${
                  isFeature ? "p-10 lg:p-12" : tall ? "p-8 lg:p-10" : "p-8"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="w-12 h-12 grid place-items-center bg-[#D97736]/10 border border-[#D97736]/30 text-[#D97736] group-hover:bg-[#D97736] group-hover:text-[#0B0C10] transition-all">
                    <Icon className="w-5 h-5" strokeWidth={2} />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A0AAB2] border border-white/10 px-2.5 py-1">
                    {data.tag}
                  </span>
                </div>

                <div className="mt-10">
                  <h3
                    className={`font-display font-bold text-white tracking-tight leading-tight mb-3 ${
                      isFeature
                        ? "text-3xl sm:text-4xl lg:text-5xl"
                        : "text-2xl sm:text-3xl"
                    }`}
                  >
                    {data.name}
                  </h3>
                  <p
                    className={`text-[#A0AAB2] leading-relaxed ${
                      isFeature ? "text-base sm:text-lg max-w-2xl" : "text-sm"
                    }`}
                  >
                    {data.desc}
                  </p>
                  <div className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white group-hover:text-[#D97736] transition-colors">
                    <span>↗</span>
                    <span className="opacity-70 group-hover:opacity-100">
                      {data.tag}
                    </span>
                  </div>
                </div>

                <ArrowUpRight className="absolute top-6 right-6 w-5 h-5 text-white/0 group-hover:text-white/60 transition-all" />
              </div>
            </a>
          );
        })}
      </div>
    </section>
  );
}
